import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { VIDEOS } from "../data/content";

export const FeaturedVisuals = () => {
  const [active, setActive] = useState(VIDEOS[0]);
  const [playing, setPlaying] = useState(false);

  return (
    <section
      id="videos"
      data-testid="videos-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16"
        >
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="h-px w-10 bg-[color:var(--gold)]" />
              <span className="overline">Featured Visuals</span>
            </div>
            <h2 className="font-condensed text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Cinematic <span className="gold-text italic font-display">Moments</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base">
            Music videos, live performances, and behind-the-scenes glimpses into the world of it&apos;s Malie.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          data-testid="featured-video-frame"
          className="relative aspect-video w-full overflow-hidden rounded-2xl border border-white/10 group"
        >
          {playing ? (
            <iframe
              key={active.id}
              src={`https://www.youtube.com/embed/${active.id}?autoplay=1&rel=0`}
              title={active.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          ) : (
            <>
              <img
                src={active.thumb}
                alt={active.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <button
                onClick={() => setPlaying(true)}
                data-testid="featured-video-play"
                className="absolute inset-0 grid place-items-center"
                aria-label="Play featured video"
              >
                <span className="grid place-items-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/10 backdrop-blur-md border border-white/20 group-hover:bg-[color:var(--gold)] group-hover:text-black transition-all">
                  <Play size={28} strokeWidth={1.5} className="ml-1" />
                </span>
              </button>
              <div className="absolute left-6 bottom-6 right-6 flex items-end justify-between">
                <div>
                  <p className="overline mb-2">Now Featuring</p>
                  <h3 className="font-display text-2xl sm:text-4xl">{active.title}</h3>
                  <p className="text-white/60 mt-1">{active.subtitle}</p>
                </div>
              </div>
            </>
          )}
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6 hidden">
          {VIDEOS.map((v, i) => (
            <motion.button
              key={v.id}
              data-testid={`video-thumb-${i}`}
              onClick={() => {
                setActive(v);
                setPlaying(false);
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`relative aspect-video overflow-hidden rounded-xl border transition-all duration-500 hover:scale-[1.02] hover:border-[color:var(--gold)] ${
                active.id === v.id
                  ? "border-[color:var(--gold)] ring-1 ring-[color:var(--gold)]/40"
                  : "border-white/10"
              }`}
            >
              <img src={v.thumb} alt={v.title} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute left-3 bottom-3 right-3 text-left">
                <p className="text-xs uppercase tracking-[0.18em] text-white/70 truncate">{v.subtitle}</p>
                <p className="font-display text-base sm:text-lg truncate">{v.title}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};
